import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
import { RoleLevel, ROLE_LABELS } from '../types/rbacImproved';
import { BaseModal, SectionTitle, Badge } from './shared/BaseModal';
const ProfileSection = styled.div `
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const ProfileGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
const ProfileField = styled.div `
  .label {
    font-size: 11px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
    font-weight: 500;
  }

  .value {
    font-size: 14px;
    color: var(--text-primary);
    min-height: 20px;
  }
`;
const StatusBadge = styled(Badge) `
  ${props => {
    switch (props.status) {
        case 'active':
            return `
          background: rgba(22, 163, 74, 0.08);
          color: #15803D;
          border: 1px solid rgba(22, 163, 74, 0.2);
        `;
        case 'suspended':
            return `
          background: rgba(220, 38, 38, 0.08);
          color: #B91C1C;
          border: 1px solid rgba(220, 38, 38, 0.2);
        `;
        case 'pending':
            return `
          background: rgba(245, 158, 11, 0.08);
          color: #B45309;
          border: 1px solid rgba(245, 158, 11, 0.2);
        `;
        default:
            return `
          background: var(--bg-tertiary);
          color: var(--text-secondary);
          border: 1px solid var(--border-color);
        `;
    }
}}
`;
const RoleBadge = styled(Badge) `
  ${props => {
    if (props.roleLevel >= RoleLevel.PLATFORM_ADMIN)
        return `
      background: rgba(220, 38, 38, 0.08);
      color: #B91C1C;
      border: 1px solid rgba(220, 38, 38, 0.2);
    `;
    if (props.roleLevel >= RoleLevel.SALES_MANAGER)
        return `
      background: var(--accent-gold-bg);
      color: var(--accent-gold);
      border: 1px solid rgba(184, 134, 11, 0.2);
    `;
    if (props.roleLevel >= RoleLevel.SENIOR_DEALER)
        return `
      background: rgba(59, 130, 246, 0.08);
      color: #1D4ED8;
      border: 1px solid rgba(59, 130, 246, 0.2);
    `;
    if (props.roleLevel >= RoleLevel.VERIFIED_DEALER)
        return `
      background: rgba(22, 163, 74, 0.08);
      color: #15803D;
      border: 1px solid rgba(22, 163, 74, 0.2);
    `;
    return `
      background: rgba(245, 158, 11, 0.08);
      color: #B45309;
      border: 1px solid rgba(245, 158, 11, 0.2);
    `;
}}
`;
const SessionList = styled.div `
  .session-item {
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    padding: 14px;
    margin-bottom: 10px;
    transition: all 0.15s ease;

    &:hover {
      border-color: var(--accent-gold);
      background: var(--accent-gold-bg);
    }

    &:last-child {
      margin-bottom: 0;
    }

    .session-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .device {
        color: var(--text-primary);
        font-size: 14px;
        font-weight: 500;
      }

      .status {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #15803D;

        &.invalid {
          color: #B91C1C;
        }
      }
    }

    .session-details {
      font-size: 12px;
      color: var(--text-secondary);

      div {
        margin-bottom: 4px;
      }
    }
  }
`;
const AuditList = styled.div `
  .audit-item {
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    padding: 14px;
    margin-bottom: 10px;
    transition: all 0.15s ease;

    &:hover {
      border-color: var(--accent-gold);
      background: var(--accent-gold-bg);
    }

    &:last-child {
      margin-bottom: 0;
    }

    .audit-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .action {
        color: var(--text-primary);
        font-size: 14px;
        font-weight: 500;
        text-transform: capitalize;
      }

      .success {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #15803D;

        &.failed {
          color: #B91C1C;
        }
      }
    }

    .audit-details {
      font-size: 12px;
      color: var(--text-secondary);
    }
  }
`;
const UserProfileModal = ({ user, isOpen, onClose, userDetails }) => {
    const formatDate = (dateString) => {
        if (!dateString)
            return 'Never';
        return new Date(dateString).toLocaleString();
    };
    const formatUserAgent = (userAgent) => {
        if (userAgent.includes('Chrome'))
            return 'Chrome Browser';
        if (userAgent.includes('Firefox'))
            return 'Firefox Browser';
        if (userAgent.includes('Safari'))
            return 'Safari Browser';
        if (userAgent.includes('Edge'))
            return 'Edge Browser';
        return 'Unknown Browser';
    };
    return (_jsx(BaseModal, { isOpen: isOpen, onClose: onClose, title: "User Profile", titleAccent: "Profile", subtitle: `Detailed information for ${user.profile.firstName} ${user.profile.lastName}`, size: "md", children: _jsxs("div", { children: [_jsxs(ProfileSection, { children: [_jsx(SectionTitle, { children: "Basic Information" }), _jsxs(ProfileGrid, { children: [_jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "Full Name" }), _jsxs("div", { className: "value", children: [user.profile.firstName, " ", user.profile.lastName] })] }), _jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "Email Address" }), _jsx("div", { className: "value", children: user.email })] }), _jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "Business Name" }), _jsx("div", { className: "value", children: user.profile.businessName || 'Not provided' })] }), _jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "User ID" }), _jsx("div", { className: "value", children: user.id })] })] })] }), _jsxs(ProfileSection, { children: [_jsx(SectionTitle, { children: "Account Status" }), _jsxs(ProfileGrid, { children: [_jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "Status" }), _jsx("div", { className: "value", children: _jsx(StatusBadge, { status: user.status, children: user.status }) })] }), _jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "Role" }), _jsx("div", { className: "value", children: _jsx(RoleBadge, { roleLevel: user.roleLevel, children: ROLE_LABELS[user.roleLevel] }) })] }), _jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "Role Level" }), _jsxs("div", { className: "value", children: ["Level ", user.roleLevel] })] }), _jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "Email Verified" }), _jsx("div", { className: "value", children: user.emailVerified ? 'Yes' : 'No' })] })] })] }), _jsxs(ProfileSection, { children: [_jsx(SectionTitle, { children: "Account Timeline" }), _jsxs(ProfileGrid, { children: [_jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "Member Since" }), _jsx("div", { className: "value", children: formatDate(user.createdAt) })] }), _jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "Last Login" }), _jsx("div", { className: "value", children: formatDate(user.lastLoginAt) })] }), _jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "Last Updated" }), _jsx("div", { className: "value", children: formatDate(userDetails?.updatedAt) })] }), _jsxs(ProfileField, { children: [_jsx("div", { className: "label", children: "Password Changed" }), _jsx("div", { className: "value", children: formatDate(userDetails?.passwordChangedAt) })] })] })] }), userDetails?.activeSessions && userDetails.activeSessions.length > 0 && (_jsxs(ProfileSection, { children: [_jsxs(SectionTitle, { children: ["Active Sessions (", userDetails.activeSessions.length, ")"] }), _jsx(SessionList, { children: userDetails.activeSessions.slice(0, 5).map((session, index) => (_jsxs("div", { className: "session-item", children: [_jsxs("div", { className: "session-header", children: [_jsx("div", { className: "device", children: formatUserAgent(session.deviceInfo?.userAgent || '') }), _jsx("div", { className: `status ${!session.isValid ? 'invalid' : ''}`, children: session.isValid ? 'Active' : 'Expired' })] }), _jsxs("div", { className: "session-details", children: [_jsxs("div", { children: ["Created: ", formatDate(session.createdAt)] }), _jsxs("div", { children: ["Last Used: ", formatDate(session.lastUsedAt)] }), _jsxs("div", { children: ["Expires: ", formatDate(session.expiresAt)] }), session.deviceInfo?.ip && _jsxs("div", { children: ["IP: ", session.deviceInfo.ip] })] })] }, index))) })] })), userDetails?.recentAuditEvents && userDetails.recentAuditEvents.length > 0 && (_jsxs(ProfileSection, { children: [_jsx(SectionTitle, { children: "Recent Activity" }), _jsx(AuditList, { children: userDetails.recentAuditEvents.slice(0, 10).map((event, index) => (_jsxs("div", { className: "audit-item", children: [_jsxs("div", { className: "audit-header", children: [_jsx("div", { className: "action", children: event.action }), _jsx("div", { className: `success ${!event.success ? 'failed' : ''}`, children: event.success ? 'Success' : 'Failed' })] }), _jsxs("div", { className: "audit-details", children: [_jsx("div", { children: formatDate(event.timestamp) }), event.ip && _jsxs("div", { children: ["IP: ", event.ip] })] })] }, index))) })] }))] }) }));
};
export default UserProfileModal;
